import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { canEditModule } from "@/utils/permissions";
import ClientCombobox from "@/components/ClientCombobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Copy, ExternalLink, RefreshCw, Pencil, Trash2, Plus, RotateCcw, Upload,
  BarChart3, Power, Wand2, Mail, Loader2, Bell, Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const shade = (hex, pct) => {
  const m = (hex || "#1F34E6").replace("#", "");
  const f = (i) => {
    const c = parseInt(m.slice(i, i + 2), 16);
    const target = pct < 0 ? 0 : 255;
    return Math.max(0, Math.min(255, Math.round(((target - c) * Math.abs(pct)) / 100) + c));
  };
  return `#${[f(0), f(2), f(4)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

const SLOTS = [
  { key: "color_base", label: "Base", rol: "Header hero, chip y subtítulo", derive: null },
  { key: "color_acento", label: "Acento", rol: "Botones y ratings seleccionados", derive: (b) => shade(b, -15) },
  { key: "color_oscuro", label: "Oscuro", rol: "Header y Footer", derive: (b) => shade(b, -55) },
  { key: "color_suave", label: "Suave", rol: "Eyebrow pill y fondos sutiles", derive: (b) => shade(b, 85) },
];

const EMPTY = {
  cliente: "", nombre: "", subtitulo: "", color_base: "#1F34E6",
  color_acento: null, color_oscuro: null, color_suave: null,
  mostrar_en: false, mostrar_fr: false, logo_cliente_url: null, logo_evento_url: null, activo: true,
  notificar_a: [],
  total_a_encuestar: 0,
};

const SendLinkDialog = ({ evento, onClose }) => {
  const [emails, setEmails] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [mostrarPD, setMostrarPD] = useState(true);
  const [sending, setSending] = useState(false);
  const send = async () => {
    const list = emails.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    if (!list.length) return toast.error("Indica al menos un correo válido");
    setSending(true);
    try {
      await axios.post(`${API}/encuestas/eventos/${evento.id}/send-link`, { emails: list, mensaje, mostrar_logo_pd: mostrarPD });
      toast.success(`Link enviado a ${list.length} destinatario(s)`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el correo");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="eq-send-link-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#0037ff]" /> Enviar link de la encuesta</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-500 -mt-2">Se enviará el link de la encuesta <b>{evento.nombre}</b> con el branding del evento. Los destinatarios van en CCO (no se ven entre sí).</p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-700">Correos de los encuestados (separados por coma)</label>
            <Textarea rows={3} value={emails} placeholder="invitado1@empresa.com, invitado2@empresa.com…"
              onChange={(e) => setEmails(e.target.value)} data-testid="eq-send-link-emails" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-700">Mensaje personalizado (opcional)</label>
            <Textarea rows={3} value={mensaje} placeholder="Gracias por acompañarnos, tu opinión nos ayuda a mejorar…"
              onChange={(e) => setMensaje(e.target.value)} data-testid="eq-send-link-mensaje" />
          </div>
          <label className="flex items-start gap-2.5 rounded-xl border border-zinc-200 bg-zinc-50/60 px-3.5 py-3 cursor-pointer">
            <Checkbox checked={mostrarPD} onCheckedChange={(v) => setMostrarPD(!!v)} className="mt-0.5" data-testid="eq-send-link-logo-pd" />
            <span className="text-sm text-zinc-600 leading-snug">
              <b>Mostrar logo PD Travel</b> en el encabezado del correo.
              <span className="block text-[11px] text-zinc-400 mt-0.5">Activado: logo PD a la izquierda y logos del evento a la derecha. Desactivado: solo los logos del evento.</span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="eq-send-link-cancelar">Cancelar</Button>
          <Button onClick={send} disabled={sending} className="bg-[#0037ff] hover:bg-[#0037ff]/90" data-testid="eq-send-link-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Enviar link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LogoUpload = ({ label, url, onFile, onClear, testid }) => (
  <div className="flex-1 min-w-[200px]">
    <p className="text-sm font-semibold text-zinc-700 mb-2">{label}</p>
    <div className="border border-dashed border-zinc-300 rounded-xl p-4 flex items-center gap-3 bg-zinc-50">
      {url ? (
        <>
          <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="h-10 object-contain rounded bg-white border border-zinc-200 px-2" />
          <button type="button" onClick={onClear} className="text-xs text-red-500 hover:underline" data-testid={`${testid}-quitar`}>Quitar</button>
        </>
      ) : (
        <label className="flex items-center gap-2 text-sm text-zinc-500 cursor-pointer hover:text-zinc-700">
          <Upload className="w-4 h-4" /> Subir PNG/JPG (máx 1MB)
          <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid={testid}
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </label>
      )}
    </div>
  </div>
);

const EventoModal = ({ open, onClose, evento, onSaved }) => {
  const isEdit = !!evento?.id;
  const [f, setF] = useState(EMPTY);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [pendingLogos, setPendingLogos] = useState({});
  const [notifEmails, setNotifEmails] = useState("");

  useEffect(() => {
    if (open) {
      setF(evento ? { ...EMPTY, ...evento } : EMPTY);
      setNotifEmails(((evento?.notificar_a) || []).join(", "));
      setOverrides(evento ? {
        color_acento: !!evento.color_acento, color_oscuro: !!evento.color_oscuro, color_suave: !!evento.color_suave,
      } : {});
      setPendingLogos({});
    }
  }, [open, evento]);

  const resolved = (key) => {
    const slot = SLOTS.find((s) => s.key === key);
    if (key === "color_base") return f.color_base || "#1F34E6";
    return (overrides[key] && f[key]) ? f[key] : slot.derive(f.color_base || "#1F34E6");
  };

  const setSlot = (key, val) => {
    setF((p) => ({ ...p, [key]: val }));
    if (key !== "color_base") setOverrides((p) => ({ ...p, [key]: true }));
  };

  const resetSlot = (key) => {
    setF((p) => ({ ...p, [key]: null }));
    setOverrides((p) => ({ ...p, [key]: false }));
  };

  const sugerirPaleta = () => {
    setF((p) => ({ ...p, color_acento: null, color_oscuro: null, color_suave: null }));
    setOverrides({});
  };

  const uploadLogo = async (tipo, file, eventoId) => {
    const fd = new FormData();
    fd.append("tipo", tipo);
    fd.append("file", file);
    const { data } = await axios.post(`${API}/encuestas/eventos/${eventoId}/logo`, fd);
    return data.url;
  };

  const handleLogoFile = (tipo, file) => {
    if (file.size > 1024 * 1024) { toast.error("El logo no debe superar 1MB"); return; }
    const preview = URL.createObjectURL(file);
    setPendingLogos((p) => ({ ...p, [tipo]: file }));
    setF((p) => ({ ...p, [`logo_${tipo}_url`]: preview }));
  };

  const save = async () => {
    if (!f.cliente.trim() || !f.nombre.trim()) { toast.error("Cliente y nombre son obligatorios"); return; }
    if (!HEX_RE.test(f.color_base || "")) { toast.error("Color base inválido (#RRGGBB)"); return; }
    setSaving(true);
    try {
      const payload = {
        cliente: f.cliente.trim(), nombre: f.nombre.trim(), subtitulo: f.subtitulo?.trim() || null,
        color_base: f.color_base,
        color_acento: overrides.color_acento ? f.color_acento : null,
        color_oscuro: overrides.color_oscuro ? f.color_oscuro : null,
        color_suave: overrides.color_suave ? f.color_suave : null,
        mostrar_en: f.mostrar_en, mostrar_fr: f.mostrar_fr, activo: f.activo,
        notificar_a: notifEmails.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@")),
        total_a_encuestar: Number(f.total_a_encuestar || 0),
      };
      let ev;
      if (isEdit) {
        const clears = {};
        if (!f.logo_cliente_url) clears.logo_cliente_url = null;
        if (!f.logo_evento_url) clears.logo_evento_url = null;
        ({ data: ev } = await axios.put(`${API}/encuestas/eventos/${evento.id}`, { ...payload, ...clears }));
      } else {
        ({ data: ev } = await axios.post(`${API}/encuestas/eventos`, payload));
      }
      for (const [tipo, file] of Object.entries(pendingLogos)) {
        await uploadLogo(tipo, file, ev.id);
      }
      toast.success(isEdit ? "Evento actualizado" : "Evento creado");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const pal = { base: resolved("color_base"), acento: resolved("color_acento"), oscuro: resolved("color_oscuro"), suave: resolved("color_suave") };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="encuesta-evento-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar evento" : "Nuevo evento"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* 1) Datos */}
          <section>
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wide mb-3">1 · Datos</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-zinc-600">Cliente *</label>
                <ClientCombobox value={f.cliente} testid="evento-input-cliente"
                  onSelect={(c) => setF({ ...f, cliente: c.nombre_comercial || c.company_name })}
                  onClear={() => setF({ ...f, cliente: "" })} />
              </div>
              <div>
                <label className="text-sm text-zinc-600">Nombre del evento *</label>
                <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} data-testid="evento-input-nombre" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-zinc-600">Subtítulo (opcional)</label>
                <Input value={f.subtitulo || ""} onChange={(e) => setF({ ...f, subtitulo: e.target.value })} data-testid="evento-input-subtitulo" />
              </div>
            </div>
          </section>

          {/* 2) Logos */}
          <section>
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wide mb-3">2 · Logos</h3>
            <div className="flex gap-4 flex-wrap">
              <LogoUpload label="Logo del cliente" url={f.logo_cliente_url} testid="evento-logo-cliente"
                onFile={(file) => handleLogoFile("cliente", file)}
                onClear={() => { setF((p) => ({ ...p, logo_cliente_url: null })); setPendingLogos((p) => { const { cliente, ...r } = p; return r; }); }} />
              <LogoUpload label="Logo del evento" url={f.logo_evento_url} testid="evento-logo-evento"
                onFile={(file) => handleLogoFile("evento", file)}
                onClear={() => { setF((p) => ({ ...p, logo_evento_url: null })); setPendingLogos((p) => { const { evento: _e, ...r } = p; return r; }); }} />
            </div>
          </section>

          {/* 3) Paleta */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wide">3 · Paleta</h3>
              <Button type="button" variant="outline" size="sm" onClick={sugerirPaleta} data-testid="evento-sugerir-paleta">
                <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Sugerir paleta
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {SLOTS.map((s) => (
                <div key={s.key} className="border border-zinc-200 rounded-xl p-3 flex items-center gap-3">
                  <input type="color" value={resolved(s.key)} onChange={(e) => setSlot(s.key, e.target.value)}
                    className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5 bg-white" data-testid={`evento-color-${s.key}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-800">{s.label}</p>
                    <p className="text-[11px] text-zinc-400 truncate">{s.rol}</p>
                    <Input value={resolved(s.key)} onChange={(e) => setSlot(s.key, e.target.value)}
                      className="h-7 mt-1 text-xs font-mono w-24" data-testid={`evento-hex-${s.key}`} />
                  </div>
                  {s.derive && overrides[s.key] && (
                    <button type="button" onClick={() => resetSlot(s.key)} title="Volver al sugerido"
                      className="text-zinc-400 hover:text-zinc-700" data-testid={`evento-reset-${s.key}`}>
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* 4) Idiomas */}
          <section>
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wide mb-3">4 · Idiomas</h3>
            <div className="flex gap-6 items-center">
              <span className="text-sm text-zinc-700 font-semibold">Español ✓</span>
              <label className="flex items-center gap-2 text-sm text-zinc-600">
                <Switch checked={f.mostrar_en} onCheckedChange={(v) => setF({ ...f, mostrar_en: v })} data-testid="evento-toggle-en" /> Inglés
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-600">
                <Switch checked={f.mostrar_fr} onCheckedChange={(v) => setF({ ...f, mostrar_fr: v })} data-testid="evento-toggle-fr" /> Francés
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-600 ml-auto">
                <Switch checked={f.activo} onCheckedChange={(v) => setF({ ...f, activo: v })} data-testid="evento-toggle-activo" /> Activo
              </label>
            </div>
          </section>

          {/* 5) Notificaciones */}
          <section>
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wide mb-3 flex items-center gap-2"><Bell className="w-4 h-4" /> 5 · Notificaciones</h3>
            <label className="text-sm text-zinc-600">Notificar cada respuesta a estos correos (separados por coma)</label>
            <Input value={notifEmails} onChange={(e) => setNotifEmails(e.target.value)}
              placeholder="equipo@pdtravelmex.com, gerente@pdtravelmex.com" data-testid="evento-input-notificar" />
            <p className="text-[11px] text-zinc-400 mt-1">Cada vez que un invitado responda la encuesta, se enviará un correo con el detalle de la respuesta. Déjalo vacío para no notificar.</p>
          </section>

          {/* 6) Meta de encuestados */}
          <section>
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wide mb-3">6 · Meta de encuestados</h3>
            <label className="text-sm text-zinc-600">Total de pasajeros a encuestar</label>
            <Input type="number" min="0" step="1" value={f.total_a_encuestar || ""}
              onChange={(e) => setF({ ...f, total_a_encuestar: Number(e.target.value || 0) })}
              placeholder="Ej. 250" data-testid="evento-input-total-encuestar" />
            <p className="text-[11px] text-zinc-400 mt-1">Se usa en el dashboard para calcular la <b>tasa de encuestas contestadas</b> vs total esperado. Déjalo en 0 para ocultar esa métrica.</p>
          </section>

          {/* Vista previa */}
          <section>
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wide mb-3">Vista previa del formulario</h3>
            <div className="rounded-xl overflow-hidden border border-zinc-200" data-testid="evento-preview">
              <div className="py-4 flex justify-center items-center gap-4" style={{ background: pal.oscuro }}>
                {f.logo_cliente_url
                  ? <img src={f.logo_cliente_url.startsWith("http") || f.logo_cliente_url.startsWith("blob") ? f.logo_cliente_url : `${process.env.REACT_APP_BACKEND_URL}${f.logo_cliente_url}`} alt="" className="h-8 object-contain" />
                  : <span className="text-white font-bold tracking-widest text-sm">{(f.cliente || "CLIENTE").toUpperCase()}</span>}
              </div>
              <div className="text-center py-6 px-4 bg-white">
                <span className="inline-block text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3" style={{ background: pal.suave, color: pal.oscuro }}>
                  {f.nombre || "Nombre del evento"}
                </span>
                <p className="text-xl font-extrabold text-zinc-900">¡Gracias por viajar con nosotros!</p>
                {f.subtitulo && <p className="text-sm font-semibold mt-1" style={{ color: pal.base }}>{f.subtitulo}</p>}
                <button type="button" className="mt-4 px-6 py-2 rounded-lg text-white text-sm font-bold" style={{ background: pal.acento }}>Enviar respuestas</button>
              </div>
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="evento-cancelar-btn">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0037ff] hover:bg-[#0037ff]/90" data-testid="evento-guardar-btn">
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear evento"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function EncuestasEventos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = canEditModule(user, "clientes_encuestas");
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, evento: null });
  const [sendLink, setSendLink] = useState(null);
  const [confirm, setConfirm] = useState(null); // { tipo: 'rotar'|'eliminar', evento }
  const [fNombre, setFNombre] = useState("");
  const [fCliente, setFCliente] = useState("todos");
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/encuestas/eventos`);
      setEventos(data);
    } catch {
      toast.error("No se pudieron cargar los eventos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publicUrl = (ev) => `${window.location.origin}/eq/${ev.public_slug}`;

  const copyLink = (ev) => {
    navigator.clipboard.writeText(publicUrl(ev));
    toast.success("Link copiado al portapapeles");
  };

  const rotarSlug = async (ev) => {
    if (!window.confirm("¿Rotar el link público? El link anterior dejará de funcionar.")) return;
    try {
      await axios.post(`${API}/encuestas/eventos/${ev.id}/rotar-slug`);
      toast.success("Link rotado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al rotar el link");
    }
  };

  const toggleActivo = async (ev) => {
    try {
      await axios.put(`${API}/encuestas/eventos/${ev.id}`, { activo: !ev.activo });
      toast.success(ev.activo ? "Evento desactivado" : "Evento activado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const eliminar = async (ev) => {
    try {
      await axios.delete(`${API}/encuestas/eventos/${ev.id}`);
      toast.success("Evento eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const esHeredado = (ev) => (ev.origen || "backoffice") !== "backoffice";

  const clientes = [...new Set(eventos.map((e) => e.cliente).filter(Boolean))].sort();
  const filtrados = eventos.filter((ev) => {
    if (fNombre.trim() && !(ev.nombre || "").toLowerCase().includes(fNombre.trim().toLowerCase())) return false;
    if (fCliente !== "todos" && ev.cliente !== fCliente) return false;
    const fecha = (ev.created_at || "").slice(0, 10);
    if (fDesde && fecha < fDesde) return false;
    if (fHasta && fecha > fHasta) return false;
    return true;
  });

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="encuestas-eventos-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-zinc-900" style={{ fontFamily: "'Mulish', sans-serif" }}>Encuestas de Calidad</h1>
          <p className="text-sm text-zinc-500 mt-1">Encuestas de satisfacción post-viaje por evento</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/clientes/encuestas/dashboard")} data-testid="ir-dashboard-btn">
            <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
          </Button>
          {canEdit && (
            <Button onClick={() => setModal({ open: true, evento: null })} className="bg-[#0037ff] hover:bg-[#0037ff]/90" data-testid="nuevo-evento-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo evento
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-4 mb-5 grid grid-cols-12 gap-3 items-end">
        <div className="col-span-12 sm:col-span-4">
          <label className="text-xs text-zinc-500 font-semibold">Nombre del evento</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input className="pl-9" placeholder="Buscar evento…" value={fNombre}
              onChange={(e) => setFNombre(e.target.value)} data-testid="filtro-nombre-evento" />
          </div>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label className="text-xs text-zinc-500 font-semibold">Empresa / Cliente</label>
          <Select value={fCliente} onValueChange={setFCliente}>
            <SelectTrigger data-testid="filtro-cliente"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas</SelectItem>
              {clientes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-6 sm:col-span-4 grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-zinc-500 font-semibold">Creado desde</label>
            <Input type="date" value={fDesde} onChange={(e) => setFDesde(e.target.value)} data-testid="filtro-creado-desde" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-semibold">Creado hasta</label>
            <Input type="date" value={fHasta} onChange={(e) => setFHasta(e.target.value)} data-testid="filtro-creado-hasta" />
          </div>
        </div>
        <div className="col-span-12 sm:col-span-1 flex justify-end">
          {(fNombre || fCliente !== "todos" || fDesde || fHasta) && (
            <Button variant="ghost" size="sm" className="text-zinc-400"
              onClick={() => { setFNombre(""); setFCliente("todos"); setFDesde(""); setFHasta(""); }} data-testid="filtro-limpiar">
              Limpiar
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-zinc-400">Cargando…</div>
      ) : filtrados.length === 0 ? (
        <div className="py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl">
          {eventos.length === 0 ? "No hay eventos aún" : "Ningún evento coincide con los filtros"}
        </div>
      ) : (
        <div className="space-y-3">
          {filtrados.map((ev) => (
            <div key={ev.id} className="bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5 flex items-center gap-4 flex-wrap" data-testid={`evento-card-${ev.codigo}`}>
              <span className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ background: ev.paleta?.base || ev.color_base }} />
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-zinc-900">{ev.nombre}</p>
                  {!ev.activo && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Inactivo</span>}
                </div>
                <p className="text-sm text-zinc-500">{ev.cliente}</p>
                <div className="flex gap-1.5 mt-1">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">ES</span>
                  {ev.mostrar_en && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">EN</span>}
                  {ev.mostrar_fr && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">FR</span>}
                </div>
              </div>
              <div className="text-right text-sm mr-2">
                <p className="font-bold text-zinc-800">{ev.respuestas_count} <span className="font-normal text-zinc-400">respuestas</span></p>
                <p className="font-bold text-zinc-800">{ev.visitas_count} <span className="font-normal text-zinc-400">visitas</span></p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" title="Copiar link público" onClick={() => copyLink(ev)} data-testid={`copiar-link-${ev.codigo}`}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" title="Abrir link" onClick={() => window.open(publicUrl(ev), "_blank")} data-testid={`abrir-link-${ev.codigo}`}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" title="Enviar link por correo" onClick={() => setSendLink(ev)} data-testid={`enviar-link-${ev.codigo}`}>
                  <Mail className="w-4 h-4 text-[#0037ff]" />
                </Button>
                {canEdit && (
                  <>
                    <Button variant="ghost" size="icon" title="Rotar link" onClick={() => setConfirm({ tipo: "rotar", evento: ev })} data-testid={`rotar-slug-${ev.codigo}`}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title={ev.activo ? "Desactivar" : "Activar"} onClick={() => toggleActivo(ev)} data-testid={`toggle-activo-${ev.codigo}`}>
                      <Power className={`w-4 h-4 ${ev.activo ? "text-emerald-600" : "text-zinc-400"}`} />
                    </Button>
                    <Button variant="ghost" size="icon" title="Editar" onClick={() => setModal({ open: true, evento: ev })} data-testid={`editar-evento-${ev.codigo}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon"
                      title={esHeredado(ev) ? "Evento heredado: información final, no se puede eliminar" : "Eliminar"}
                      onClick={() => setConfirm({ tipo: "eliminar", evento: ev })}
                      disabled={esHeredado(ev)} data-testid={`eliminar-evento-${ev.codigo}`}>
                      <Trash2 className={`w-4 h-4 ${esHeredado(ev) ? "text-zinc-300" : "text-red-500"}`} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <EventoModal open={modal.open} evento={modal.evento} onClose={() => setModal({ open: false, evento: null })} onSaved={load} />
      {sendLink && <SendLinkDialog evento={sendLink} onClose={() => setSendLink(null)} />}

      {/* Confirmaciones (rotar / eliminar) */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="eq-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.tipo === "rotar" ? "¿Rotar el link público?" : `¿Eliminar el evento "${confirm?.evento?.nombre}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.tipo === "rotar"
                ? "El link anterior dejará de funcionar y se generará uno nuevo."
                : confirm?.evento?.respuestas_count > 0
                  ? `Se eliminarán también sus ${confirm.evento.respuestas_count} respuesta(s) y el historial de visitas. Esta acción no se puede deshacer.`
                  : "Esta acción no se puede deshacer."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="eq-confirm-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={confirm?.tipo === "eliminar" ? "bg-red-600 hover:bg-red-700" : "bg-[#0037ff] hover:bg-[#0037ff]/90"}
              onClick={() => { const c = confirm; setConfirm(null); c?.tipo === "rotar" ? rotarSlug(c.evento) : eliminar(c.evento); }}
              data-testid="eq-confirm-aceptar">
              {confirm?.tipo === "rotar" ? "Rotar link" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
